/**
 * Module 36 (WP-36.11) — a dev-only timing readout, drawn over the game.
 *
 * It exists for one job the desk cannot do: WP-36.10's numbers were all measured
 * in Node on a desktop, and the bug this module is chasing was reported on an
 * Android device. This puts the same numbers on that device's screen, so the
 * on-device pass produces readings rather than impressions.
 *
 * Three deliberate choices:
 *
 * - **Plain DOM, not React.** The HUD's React root re-renders on every plugin
 *   state change and allocates a fresh props object each time (WP-36.10 F7);
 *   hanging a 60fps frame-time readout off it would have the measuring
 *   instrument contribute to what it measures. This owns one element and writes
 *   `textContent`.
 * - **Not internationalized.** Same rule the `/dev/blockjam` Studio already
 *   follows: it is a diagnostic for whoever is tuning the game, never player-
 *   visible text, so the module's `t('key')` rule does not reach it.
 * - **No analytics event.** A dev tool must not add a row to
 *   `Analytics_Master_Schema.md`.
 *
 * Nothing here runs unless the host explicitly enables it — see
 * `BlockJamPlugin`'s `perfOverlay` constructor option.
 */
/** How many generations the rolling mean looks back over. */
const GEN_WINDOW = 20;
/** How many frames the frame-time readout averages. */
const FRAME_WINDOW = 60;
export class PerfOverlay {
    constructor() {
        this.el = null;
        this.raf = null;
        this.genMs = [];
        this.lastGen = null;
        this.genWorst = 0;
        this.genCount = 0;
        this.frames = [];
        this.frameWorst = 0;
        this.lastFrameMs = null;
        /** Levels entered, and how many of them were served from the idle cache. */
        this.levels = 0;
        this.levelsPrebuilt = 0;
        this.lastLevel = null;
    }
    mount(parent) {
        if (this.el || typeof document === 'undefined')
            return;
        const el = document.createElement('div');
        el.className = 'bj-perf-overlay';
        // Inline, so the overlay needs no stylesheet and cannot be affected by one.
        el.setAttribute('style', [
            'position:fixed',
            'top:calc(env(safe-area-inset-top, 0px) + 4px)',
            'left:4px',
            'z-index:9999',
            'pointer-events:none',
            'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace',
            'white-space:pre',
            'color:#7CFFB2',
            'background:rgba(0,0,0,0.72)',
            'padding:5px 7px',
            'border-radius:6px',
            'border:1px solid rgba(124,255,178,0.35)',
        ].join(';'));
        parent.appendChild(el);
        this.el = el;
        this.startFrameLoop();
        this.paint();
    }
    /** One completed level generation, whether it ran at idle or on the click. */
    recordGeneration(ms, sample) {
        this.lastGen = { ms, sample };
        this.genCount++;
        this.genWorst = Math.max(this.genWorst, ms);
        this.genMs.push(ms);
        if (this.genMs.length > GEN_WINDOW)
            this.genMs.shift();
        this.paint();
    }
    /** A level was entered — `prebuilt` is the number that matters for WP-36.11. */
    recordLevelEntered(level, prebuilt) {
        this.levels++;
        if (prebuilt)
            this.levelsPrebuilt++;
        this.lastLevel = { level, prebuilt };
        this.paint();
    }
    destroy() {
        if (this.raf !== null && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(this.raf);
        }
        this.raf = null;
        this.el?.parentElement?.removeChild(this.el);
        this.el = null;
    }
    /**
     * The frame-time sampler. Uses its own `requestAnimationFrame` rather than
     * PixiJS's ticker so it measures the whole page's frame budget — HUD renders
     * and background repaints included, not just the canvas.
     */
    startFrameLoop() {
        if (typeof requestAnimationFrame !== 'function')
            return;
        const tick = (now) => {
            if (this.lastFrameMs !== null) {
                const dt = now - this.lastFrameMs;
                this.frames.push(dt);
                if (this.frames.length > FRAME_WINDOW)
                    this.frames.shift();
                // Ignore the first second, where a mount stall would pin the worst
                // case at a number that says nothing about steady-state play.
                if (this.frames.length === FRAME_WINDOW)
                    this.frameWorst = Math.max(this.frameWorst, dt);
            }
            this.lastFrameMs = now;
            // Repaint at ~4Hz: the numbers are rolling averages, and a 60Hz text
            // update would be its own measurable cost.
            if (this.frames.length % 15 === 0)
                this.paint();
            this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }
    paint() {
        const el = this.el;
        if (!el)
            return;
        const mean = (xs) => xs.length === 0 ? 0 : xs.reduce((s, x) => s + x, 0) / xs.length;
        const frameMean = mean(this.frames);
        const fps = frameMean > 0 ? 1000 / frameMean : 0;
        const gen = this.lastGen;
        const lines = [
            'BJ PERF',
            gen
                ? `gen  last ${gen.ms.toFixed(0)}ms ${gen.sample.pregenerated ? '(idle)' : '(ON CLICK)'}`
                : 'gen  last —',
            `     mean ${mean(this.genMs).toFixed(0)}ms  worst ${this.genWorst.toFixed(0)}ms  n=${this.genCount}`,
            `next ${this.levels === 0 ? '—' : `${this.levelsPrebuilt}/${this.levels} prebuilt`}${this.lastLevel ? `  last ${this.lastLevel.prebuilt ? 'instant' : 'BUILT'}` : ''}`,
            `fps  ${fps.toFixed(0)}  frame ${frameMean.toFixed(1)}ms  worst ${this.frameWorst.toFixed(0)}ms`,
            gen ? `L${gen.sample.level} · ${gen.sample.blocks} blocks` : '',
        ];
        el.textContent = lines.filter(Boolean).join('\n');
    }
}
